import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { request } from "shared/api/request"
import { ExportsAPIType, serializer } from "shared/api/serializer/serializer"
import {
  ATTENTION_TOAST_TIMEOUT,
  DEFAULT_TOAST_TIMEOUT
} from "shared/constants/app-constants"
import { repeatUntil } from "shared/lib/repeatUntil"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { FileExport } from "shared/types/Export"
import { ToastType } from "shared/types/Toast"

export type MusterheftExportSelection =
  | { exportAll: true }
  | { exportAll: false; positionIDs: string[] }

function buildMusterheftExportUrl(
  projectID: string,
  selection: MusterheftExportSelection,
  isShareID: boolean
): string {
  const baseUrl = `${import.meta.env.VITE_API_BASE}/project-list${
    isShareID ? "/share" : ""
  }/projects/${projectID}/sample-booklet`

  // The endpoint exports every position when no positionIds are given, so the
  // "export all" case sends no query parameters at all.
  if (selection.exportAll) {
    return baseUrl
  }

  // The IDs are passed as a single comma-separated value rather than a repeated
  // parameter to keep the URL short for projects with many positions.
  const params = new URLSearchParams({
    positionIds: selection.positionIDs.join(",")
  })

  return `${baseUrl}?${params}`
}

export function useMusterheftExport(projectID: string, isShareID = false) {
  const { t } = useTranslation()

  const fetchMusterheftData = useEvent(
    async (selection: MusterheftExportSelection): Promise<FileExport> => {
      const fetchUrl = buildMusterheftExportUrl(projectID, selection, isShareID)

      return request(fetchUrl, {
        headers: { Accept: "application/*" }
      }).then(response =>
        response
          .json()
          .then(json => serializer.deserialize(ExportsAPIType, json))
      )
    }
  )

  const exportMusterheft = useEvent(
    async (selection: MusterheftExportSelection) => {
      const loadingToast = raiseToast({
        type: ToastType.LOADING,
        message: t("MusterheftExport.Loading"),
        autoClose: false
      })

      function handleSuccess() {
        toast.update(loadingToast, {
          autoClose: DEFAULT_TOAST_TIMEOUT,
          data: {
            type: ToastType.SUCCESS,
            message: t("MusterheftExport.Success")
          }
        })
      }

      function handleFailure(uiMessage: string | null) {
        toast.update(loadingToast, {
          autoClose: DEFAULT_TOAST_TIMEOUT,
          data: {
            type: ToastType.ERROR,
            timeout: ATTENTION_TOAST_TIMEOUT,
            message: `${t("MusterheftExport.Failure")} ${uiMessage}`
          }
        })
      }

      await repeatUntil(
        () => fetchMusterheftData(selection),
        (result: FileExport) => result.isComplete,
        (result: FileExport) => {
          if (result.url) {
            const tempLink = document.createElement("a")
            tempLink.href = result.url
            tempLink.click()
            tempLink.remove()
            handleSuccess()
            return Promise.resolve()
          }
          return Promise.reject(new Error("No Download URL given."))
        },
        {
          initialInterval: 1000,
          timeout: null,
          failurePredicateFn: (result: FileExport) => result.error?.message
        }
      ).promise.catch(handleFailure)
    }
  )

  return { exportMusterheft }
}
